/**
 * ehr#728 (COLLATERAL_CONTACT_NOTE_10-30-V5): what a Collateral contact note has of its own on the
 * note engine. Its layout (Session details, What happened, Next step; no Safety) and its billing
 * (90846, filled when the note opens) were already in place; V5 adds the contact phrase pills, the
 * Generate Notes hint, a read-only Session type, a rail of Generate Notes and Last session only,
 * and Note settings' Side cards tab. Pure, so the editor and the tests read the same rules.
 */
import {
  cardsHiddenFor,
  kindCardsHiddenFor,
  type QuickPrefs,
  type QuickSideCard,
} from '@ehr/shared';
import type { SideCardsTab } from './quickSettings.js';
import { kindRailCardOn } from './skillsMedNote.js';

export const COLLATERAL_KIND = 'Collateral contact';
export const isCollateral = (kind: string): boolean => kind === COLLATERAL_KIND;

/**
 * V5 (V3 bug check, IS_DRAWER phrases): contact phrases in What happened and Next step, in place of
 * the therapy phrases, by the box's body key.
 */
export const COLLATERAL_PHRASES: Readonly<Record<string, readonly string[]>> = {
  subjective: [
    'Spoke with __ about __.',
    "Shared __ with __, with the client's signed release.",
    '__ reported that the client __.',
  ],
  plan: ['Next contact with __ is set for __.', 'Will send __ to __ by __.'],
};

/** V5 (V3): the Generate Notes box names the two boxes it fills, no interventions or goals. */
export const COLLATERAL_ROUGH_PLACEHOLDER =
  "Type, paste or dictate the contact's rough notes. AI fills What happened and Next step.";

/**
 * The rail cards (V5 Notes drawer V1): a Collateral contact note keeps Generate Notes and Last
 * session; it has no Quick add, Measures or My changes. Other kinds with a rail of their own
 * (Skills / rehab, ehr#729) are in skillsMedNote's table.
 */
export type RailCardKey = 'quick' | 'measures' | 'last' | 'changes' | 'gen';
export const COLLATERAL_SIDE_CARDS: readonly QuickSideCard[] = ['last', 'gen'];
export function railCardOn(kind: string, card: RailCardKey): boolean {
  if (!isCollateral(kind)) return kindRailCardOn(kind, card);
  return card === 'last' || card === 'gen';
}

/**
 * V5 (V3 Side cards): on this note Note settings' Quick add tab is "Side cards", with only the
 * Show switches for the cards it has (Show Last session, Show Generate Notes); undefined on the
 * notes that keep the Quick add tab. They are saved for this kind only (V5 kept them in the note's
 * own storage), never in the Quick add settings the other notes read.
 */
export function sideCardsOf(kind: string): SideCardsTab | undefined {
  return isCollateral(kind) ? { kind, cards: COLLATERAL_SIDE_CARDS } : undefined;
}

/**
 * The rail cards switched off on this note: a note with its own Side cards reads that kind's
 * switches; every other note the Quick add tab's (ehr#682 V130).
 */
export function railCardsOff(prefs: QuickPrefs, contactId: string, kind: string): QuickSideCard[] {
  return sideCardsOf(kind)
    ? kindCardsHiddenFor(prefs, contactId, kind)
    : cardsHiddenFor(prefs, contactId);
}
